// frontend/src/api/metaRegistriesApi.ts
import { apiRequest, API_ENDPOINTS } from '../config/api';
import type { MetaRegistryEntry, MetaRegistryEntryKind, MetaRegistryType, DiagramSummary } from '../types/systems';

// ─── Registry Types ───────────────────────────────────────────────────────────

export async function listRegistryTypes(projectId: string): Promise<MetaRegistryType[]> {
  return apiRequest(API_ENDPOINTS.registries.list(projectId), { method: 'GET' }) ?? [];
}

export async function getRegistryType(projectId: string, slug: string): Promise<MetaRegistryType> {
  return apiRequest(API_ENDPOINTS.registries.getBySlug(projectId, slug), { method: 'GET' });
}

export async function createRegistryType(projectId: string, data: Partial<MetaRegistryType>): Promise<MetaRegistryType> {
  return apiRequest(API_ENDPOINTS.registries.create(projectId), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRegistryType(projectId: string, slug: string, data: Partial<MetaRegistryType>): Promise<MetaRegistryType> {
  return apiRequest(API_ENDPOINTS.registries.update(projectId, slug), {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRegistryType(projectId: string, slug: string): Promise<void> {
  await apiRequest(API_ENDPOINTS.registries.delete(projectId, slug), { method: 'DELETE' });
}

// ─── Entry Kinds ──────────────────────────────────────────────────────────────

export async function listEntryKinds(projectId: string, slug: string): Promise<MetaRegistryEntryKind[]> {
  return apiRequest(`${API_ENDPOINTS.registries.getBySlug(projectId, encodeURIComponent(slug))}/kinds`, { method: 'GET' }) ?? [];
}

export async function createEntryKind(projectId: string, slug: string, data: Partial<MetaRegistryEntryKind>): Promise<MetaRegistryEntryKind> {
  return apiRequest(`${API_ENDPOINTS.registries.getBySlug(projectId, encodeURIComponent(slug))}/kinds`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEntryKind(projectId: string, slug: string, id: string, data: Partial<MetaRegistryEntryKind>): Promise<MetaRegistryEntryKind> {
  return apiRequest(`${API_ENDPOINTS.registries.getBySlug(projectId, encodeURIComponent(slug))}/kinds/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEntryKind(projectId: string, slug: string, id: string): Promise<void> {
  await apiRequest(`${API_ENDPOINTS.registries.getBySlug(projectId, encodeURIComponent(slug))}/kinds/${id}`, { method: 'DELETE' });
}

// ─── Registry Entries ─────────────────────────────────────────────────────────

export interface ListEntriesParams {
  parent_id?: string;
  status?: string;
  team_id?: string;
  all?: boolean;
}

export async function listEntries(
  projectId: string,
  slug: string,
  params: ListEntriesParams = {}
): Promise<MetaRegistryEntry[]> {
  const qs = new URLSearchParams();
  if (params.parent_id) qs.set('parent_id', params.parent_id);
  if (params.status) qs.set('status', params.status);
  if (params.team_id) qs.set('team_id', params.team_id);
  if (params.all) qs.set('all', 'true');
  const query = qs.toString() ? `?${qs}` : '';
  return apiRequest(`${API_ENDPOINTS.registries.entries(projectId, encodeURIComponent(slug))}${query}`, { method: 'GET' }) ?? [];
}

export async function getEntryById(projectId: string, slug: string, id: string): Promise<MetaRegistryEntry> {
  return apiRequest(API_ENDPOINTS.registries.entryById(projectId, slug, id), { method: 'GET' });
}

export async function getEntryByCode(projectId: string, slug: string, code: string): Promise<MetaRegistryEntry | null> {
  try {
    return await apiRequest(API_ENDPOINTS.registries.entryByCode(projectId, slug, code), { method: 'GET' });
  } catch (e: any) {
    if (e.message?.includes('404')) return null;
    throw e;
  }
}

export async function createEntry(projectId: string, slug: string, data: Partial<MetaRegistryEntry>): Promise<MetaRegistryEntry> {
  return apiRequest(API_ENDPOINTS.registries.entries(projectId, encodeURIComponent(slug)), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEntry(projectId: string, slug: string, id: string, data: Partial<MetaRegistryEntry>): Promise<MetaRegistryEntry> {
  return apiRequest(API_ENDPOINTS.registries.entryById(projectId, slug, id), {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEntry(projectId: string, slug: string, id: string): Promise<void> {
  await apiRequest(API_ENDPOINTS.registries.entryById(projectId, slug, id), { method: 'DELETE' });
}

export async function getEntryChildren(projectId: string, slug: string, id: string): Promise<MetaRegistryEntry[]> {
  return apiRequest(API_ENDPOINTS.registries.entryChildren(projectId, slug, id), { method: 'GET' }) ?? [];
}

export async function getEntryDiagrams(projectId: string, slug: string, id: string): Promise<DiagramSummary[]> {
  const docs = await apiRequest(
    API_ENDPOINTS.registries.entryDiagrams(projectId, slug, id),
    { method: 'GET' }
  );
  // Returns DiagramDocument[] — map to summary shape
  return (docs ?? []).map((d: any) => ({ doc_id: d.doc_id, user_id: d.user_id, meta: d.meta }));
}

export async function getEntryRelations(projectId: string, slug: string, id: string): Promise<unknown[]> {
  return apiRequest(API_ENDPOINTS.registries.entryRelations(projectId, slug, id), { method: 'GET' }) ?? [];
}

export interface SearchEntryResult extends MetaRegistryEntry {
  ancestors: MetaRegistryEntry[];
}

export async function searchEntries(params: {
  q?: string;
  slugs?: string[];
  kinds?: string[];
  limit?: number;
} = {}): Promise<SearchEntryResult[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.slugs?.length) qs.set('slugs', params.slugs.join(','));
  if (params.kinds?.length) qs.set('kinds', params.kinds.join(','));
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs}` : '';
  return apiRequest(`/entries/search${query}`, { method: 'GET' }) ?? [];
}

// ─── GitLab Sync ──────────────────────────────────────────────────────────────

export async function publishEntry(
  projectId: string,
  slug: string,
  id: string,
  author: { author_name?: string; author_email?: string } = {}
): Promise<void> {
  await apiRequest(API_ENDPOINTS.registries.publishEntry(projectId, slug, id), {
    method: 'POST',
    body: JSON.stringify(author),
  });
}

export async function publishRegistry(
  projectId: string,
  slug: string,
  authorName = '',
  authorEmail = '',
): Promise<{ published: number; errors: string[] }> {
  return apiRequest(`${API_ENDPOINTS.registries.getBySlug(projectId, encodeURIComponent(slug))}/publish`, {
    method: 'POST',
    body: JSON.stringify({ author_name: authorName, author_email: authorEmail }),
  });
}

export async function pullEntry(projectId: string, slug: string, id: string): Promise<void> {
  await apiRequest(API_ENDPOINTS.registries.pullEntry(projectId, slug, id), {
    method: 'POST',
  });
}

// ─── Relations ────────────────────────────────────────────────────────────────

export interface RelationType {
  id: string;
  name: string;
  slug: string;
  description?: string;
  is_directed: boolean;
  is_active: boolean;
}

export interface Relation {
  id: string;
  from_entry_id: string;
  to_entry_id: string;
  relation_type_id: string;
  from_entry?: MetaRegistryEntry;
  to_entry?: MetaRegistryEntry;
  relation_type?: RelationType;
  created_by?: string;
  created_at?: string;
}

export async function listRelationTypes(projectId: string): Promise<RelationType[]> {
  return apiRequest(API_ENDPOINTS.relations.types(projectId), { method: 'GET' }) ?? [];
}

export async function createRelationType(projectId: string, data: Partial<RelationType>): Promise<RelationType> {
  return apiRequest(API_ENDPOINTS.relations.createType(projectId), { method: 'POST', body: JSON.stringify(data) });
}

export async function updateRelationType(projectId: string, id: string, data: Partial<RelationType>): Promise<RelationType> {
  return apiRequest(API_ENDPOINTS.relations.updateType(projectId, id), { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteRelationType(projectId: string, id: string): Promise<void> {
  await apiRequest(API_ENDPOINTS.relations.deleteType(projectId, id), { method: 'DELETE' });
}

export async function listRelations(projectId: string, params: { from?: string; to?: string } = {}): Promise<Relation[]> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const query = qs.toString() ? `?${qs}` : '';
  return apiRequest(`${API_ENDPOINTS.relations.list(projectId)}${query}`, { method: 'GET' }) ?? [];
}

export async function createRelation(projectId: string, data: {
  from_entry_id: string;
  to_entry_id: string;
  relation_type_id: string;
}): Promise<Relation> {
  return apiRequest(API_ENDPOINTS.relations.create(projectId), { method: 'POST', body: JSON.stringify(data) });
}

export async function deleteRelation(projectId: string, id: string): Promise<void> {
  await apiRequest(API_ENDPOINTS.relations.delete(projectId, id), { method: 'DELETE' });
}
